import {
  LayoutDashboard, Truck, Users, Users2, Shirt, Layers, Ruler, Hash,
  Tag, Box, Palette, Scissors, Package, ShoppingCart,
  PackageOpen, FileText, Building2, MapPin, Warehouse, UserCog,
  ClipboardList, GitBranch, PackageCheck, Boxes, Briefcase, ShieldCheck,
  BarChart3, HardHat, ClipboardCheck, Settings, TrendingUp, GraduationCap, Wallet,
  Receipt, Clock, DollarSign, HelpCircle, Activity, Database, RotateCcw,
  // Phase 36by — Percent icon for the Rate Difference menu entry
  Percent,
  CalendarClock,
  CreditCard,
} from "lucide-react";

/**
 * Sidebar navigation.
 *
 * Each item's `module` field maps to the backend PageModule enum value.
 * Sidebar.jsx uses this + useAuth().hasPermission(module, "VIEW") to hide
 * items the user can't access.  A missing module means "always visible".
 *
 * Keep these `module` strings in sync with com.smit.uniform.enums.PageModule.
 */
const NAV = [
  {
    group: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, module: "DASHBOARD" },
    ],
  },
  {
    group: "Masters",
    items: [
      { id: "vendors",         label: "Vendors",         icon: Truck,        module: "VENDOR" },
      { id: "customers",       label: "Customers",       icon: Users,        module: "CUSTOMER" },
      { id: "customer-groups", label: "Customer Groups", icon: Users2,       module: "CUSTOMER_GROUP" },
      { id: "salesmen",        label: "Salesmen",        icon: Briefcase,    module: "SALESMAN" },
      { id: "items",           label: "Items",           icon: Shirt,        module: "ITEM" },
      { id: "cloth",           label: "Cloth",           icon: Layers,       module: "CLOTH" },
      { id: "cloth-variants",  label: "Cloth Variants",  icon: Palette,      module: "CLOTH_VARIANT" },
      { id: "uom",             label: "UOM",             icon: Ruler,        module: "UOM" },
      // Phase 36da.99.uc — UoM Chain master (e.g. Gross → Packet → Button).
      { id: "uom-chain",       label: "UOM Chain",       icon: GitBranch,    module: "UOM_CHAIN" },
      { id: "panna",           label: "Panna",           icon: Hash,         module: "PANNA" },
      { id: "matching",        label: "Matching",        icon: Tag,          module: "MATCHING" },
      { id: "material",        label: "Material",        icon: Box,          module: "MATERIAL" },
      // Stitching operations and the types beneath them. Groundwork for the
      // operation-wise work that will sit on top of it.
      { id: "stitching-operations", label: "Stitching Operations", icon: Scissors, module: "STITCHING_OPERATION" },
      { id: "material-sets",  label: "Material Sets",  icon: Package,  module: "MATERIAL_SET" },
      // Phase 36da.99.mv — Material variant master (brand + size options).
      { id: "material-variants", label: "Material Variants", icon: Tag,        module: "MATERIAL_VARIANT" },
      { id: "color",           label: "Colors",          icon: Palette,      module: "COLOR" },
      { id: "schools",         label: "Schools",         icon: GraduationCap,module: "SCHOOL" },
      { id: "class-masters",   label: "Class Master",    icon: GraduationCap,module: "CLASS_MASTER" },
      { id: "size-distribution-formula", label: "Size Distribution Formula", icon: GraduationCap, module: "SIZE_DISTRIBUTION_FORMULA" },
      { id: "size-distributions",        label: "Size Distribution",         icon: GraduationCap, module: "SIZE_DISTRIBUTION" },
      { id: "expense-masters",           label: "Expense Master",            icon: Receipt,       module: "EXPENSE_MASTER" },
      { id: "employees",       label: "Employees",       icon: Users,        module: "EMPLOYEE" },
      { id: "attendance",      label: "Attendance",      icon: Clock,        module: "ATTENDANCE" },
      { id: "attendance-register", label: "Attendance Register", icon: ClipboardList, module: "ATTENDANCE" },
      { id: "attendance-log",  label: "Raw Punch Log",   icon: ClipboardList,module: "ATTENDANCE" },
      { id: "attendance-policy", label: "Attendance Policy", icon: Settings,     module: "ATTENDANCE_POLICY" },
      { id: "official-holidays", label: "Official Holidays", icon: CalendarClock, module: "OFFICIAL_HOLIDAY" },
      { id: "brands",          label: "Brands",          icon: Tag,          module: "BRAND" },
      { id: "services",        label: "Services",        icon: Settings,     module: "SERVICE_MASTER" },
      { id: "reasons",         label: "Reasons",         icon: HelpCircle,   module: "REASON_MASTER" },
    ],
  },
  {
    group: "Production",
    items: [
      { id: "patterns",        label: "Patterns",        icon: Scissors,     module: "PATTERN" },
      { id: "products",        label: "Products",        icon: Package,      module: "PRODUCT" },
      { id: "departments",     label: "Departments",     icon: GitBranch,    module: "DEPARTMENT" },
      { id: "artwork-request", label: "Artwork Request", icon: Palette,      module: "ARTWORK_REQUEST" },
      { id: "cutting-issues",  label: "Cutting Issues",  icon: Scissors,     module: "CUTTING_ISSUE" },
      { id: "cutting-inwards", label: "Cutting Inward",  icon: PackageCheck, module: "CUTTING_INWARD" },
      { id: "dept-flow",       label: "Dept Flow",       icon: Boxes,        module: "DEPT_FLOW" },
      { id: "material-outward", label: "Material Outward", icon: PackageOpen, module: "MATERIAL_OUTWARD" },
    ],
  },
  {
    group: "Contractors",
    items: [
      { id: "contractors",               label: "Contractors",       icon: HardHat,        module: "CONTRACTOR" },
      { id: "contractor-issues",         label: "Contractor Issues", icon: ClipboardCheck, module: "CONTRACTOR_ISSUE" },
      { id: "contractor-inwards",        label: "Contractor Inward", icon: PackageCheck,   module: "CONTRACTOR_INWARD" },
      // Phase 36da.99.dsr — Daily Stitching Report (Cut to Pack / Stitching Out /
      // Stitching only).  Reuses CONTRACTOR_ISSUE permission: it is a view over
      // contractor issues, and a brand-new module key would 403 for every user
      // until someone granted it by hand.
      { id: "daily-stitching-report",    label: "Daily Stitching Report", icon: BarChart3, module: "DAILY_STITCHING_REPORT" },
    ],
  },
  {
    group: "Orders",
    items: [
      { id: "purchase-orders",       label: "Purchase Orders (All)",  icon: ShoppingCart, module: "PURCHASE_ORDER" },
      { id: "cloth-purchase-orders", label: "Cloth Purchase Orders",  icon: ShoppingCart, module: "PURCHASE_ORDER" },
      { id: "material-purchase-orders", label: "Material Purchase Orders", icon: ShoppingCart, module: "PURCHASE_ORDER" },
      { id: "grn",                label: "GRN (All)",         icon: PackageOpen,  module: "GRN" },
      { id: "cloth-grn",          label: "Cloth GRN",         icon: PackageOpen,  module: "GRN" },
      { id: "material-grn",       label: "Material GRN",      icon: PackageOpen,  module: "GRN" },
      // Phase 36da.99.bp — Vendor billing layer.
      // Purchase Invoice consolidates one-or-more confirmed GRNs into a
      // single vendor bill and auto-posts a credit to the vendor ledger.
      // Vendor Ledger is the read-only running-balance view per vendor.
      { id: "purchase-invoices",  label: "Purchase Invoices", icon: Receipt,      module: "PURCHASE_INVOICE" },
      { id: "debit-notes",        label: "Debit Notes",       icon: RotateCcw,    module: "DEBIT_NOTE" },
      { id: "bale-opening",       label: "Bale Opening",      icon: Layers,       module: "BALE_OPENING" },
      // Phase 37 — ORDER FORM.  The manufacturing instruction: scan a barcode
      // from the quotation, enter size-wise qty, cloth and cutting instructions.
      // Sits before Sale Orders because that is the order of the real workflow —
      // the floor is told what to make before the sale is booked.
      { id: "order-forms",        label: "Order Forms",       icon: ClipboardCheck, module: "ORDER_FORM" },
      { id: "sale-orders",        label: "Sale Orders",       icon: FileText,     module: "SALE_ORDER" },
    // Garment labels — printed FROM a sale order, so it sits directly
    // under it. Same SALE_ORDER module: anyone who can see an order
    // can label its pieces.
    { id: "garment-labels",    label: "Garment Labels",    icon: Tag,          module: "GARMENT_LABEL" },
      { id: "packing-slips",      label: "Packing Slips",     icon: Package,      module: "PACKING_SLIP" },
      { id: "delivery-challans",  label: "Delivery Challans", icon: FileText,     module: "DELIVERY_CHALLAN" },
      { id: "dc-barcode-register",label: "DC Barcode Register", icon: Hash,       module: "DELIVERY_CHALLAN_BARCODE" },
      { id: "quotations",         label: "Quotations",        icon: ClipboardList,module: "QUOTATION" },
      { id: "quotation-request",  label: "Quotation Requests",icon: ClipboardList,module: "QUOTATION_REQUEST" },
      // Phase 36da.26 — Sample Request module: customer asks for samples
      // against a quotation; auto-flips to PUNCHED when a Sample-type
      // Sale Order is created.
      { id: "sample-request",     label: "Sample Requests",  icon: ClipboardList,module: "SAMPLE_REQUEST" },
      { id: "barcode-quotations", label: "Barcode Register",  icon: Hash,         module: "QUOTATION" },
      { id: "stock",              label: "Stock",             icon: BarChart3,    module: "STOCK" },
      { id: "stock-ledger",       label: "Stock Ledger",      icon: BarChart3,    module: "STOCK" },
      { id: "manual-garment-stock", label: "Manual Stock Entry", icon: Database,  module: "MANUAL_GARMENT_STOCK" },
      // Phase 36da.72 — per-vendor / per-school cloth stock entries + report
      { id: "manual-cloth-stock", label: "Manual Cloth Stock", icon: Database,    module: "MANUAL_CLOTH_STOCK" },
      { id: "cloth-stock-report", label: "Cloth Stock Report", icon: BarChart3,   module: "MANUAL_CLOTH_STOCK" },
      // Phase 36da.99.bp — manual material stock entry, mirror of cloth.
      { id: "manual-material-entry", label: "Manual Material Entry", icon: Database, module: "MANUAL_MATERIAL_ENTRY" },
      // Directly beneath Manual Material Entry: the same material, its
      // movement history. Same STOCK permission as the cloth ledger.
      { id: "material-stock-ledger", label: "Material Stock Ledger", icon: BarChart3, module: "STOCK" },
      { id: "goods-return",       label: "Goods Return",      icon: RotateCcw,    module: "GOODS_RETURN" },
      // Phase 36by — Rate Difference module.  Reuses GOODS_RETURN
      // permission group so any role with goods-return access also gets
      // rate-diff access (intentional — same accounting team handles both).
      { id: "rate-difference",    label: "Rate Difference",   icon: Percent,      module: "GOODS_RETURN" },
      // Phase 36da.99.ef — GR Barcode Register (mirror of DC Barcode Register).
      { id: "gr-barcode-register", label: "GR Barcode Register", icon: Hash,       module: "GR_BARCODE_REGISTER" },
    ],
  },
  {
    group: "Payments & Ledger",
    items: [
      { id: "job-work-entry",    label: "Job Work Entry",    icon: ClipboardCheck,module: "JOB_WORK_ENTRY" },
      { id: "job-work-report",   label: "Job Work Report",   icon: FileText,      module: "JOB_WORK_REPORT" },
      { id: "fixed-salary-entry", label: "Fixed Salary",     icon: CalendarClock, module: "FIXED_SALARY_ENTRY" },
      { id: "fixed-salary-report", label: "Fixed Salary Report", icon: FileText,  module: "FIXED_SALARY_ENTRY" },
      // Phase 36ca — Employee Salary Increment register.  TrendingUp
      // icon (already imported) reads as "salary going up".  Reuses
      // EMPLOYEE permission since it's part of staff management.
      { id: "employee-increment", label: "Increment Register",icon: TrendingUp,    module: "EMPLOYEE" },
      // ONE entry screen for every payment: staff, contractor, vendor,
      // transport, hundekari, expenses — plus customer receipts.
      { id: "payment-hub",       label: "Payment Entry",     icon: CreditCard,    module: "PAYMENT_HUB" },
      // Every party ledger behind one searchable dropdown.
      { id: "ledger-hub",        label: "Ledger",            icon: Wallet,        module: "LEDGER_HUB" },
      { id: "all-payments-register", label: "All Payments Register", icon: Receipt, module: "PAYMENT_REGISTER" },
    ],
  },
  {
    group: "Invoices",
    items: [
      // Single-firm: ONE tax invoice per delivery challan. The ids keep their
      // "-l1" suffix so saved links and open tabs still resolve.
      { id: "invoice-register-l1", label: "Invoices",       icon: Receipt,   module: "INVOICE_REGISTER" },
      { id: "ageing-report-l1",    label: "Ageing Report",  icon: Clock,     module: "AGEING_REPORT" },
      { id: "invoice-settings",    label: "Invoice Settings",  icon: Settings,  module: "INVOICE_SETTINGS" },
    ],
  },
  {
    group: "Reports",
    items: [
      { id: "so-tracking",         label: "SO Tracking",         icon: Activity, module: "SO_TRACKING" },
      { id: "product-so-tracking", label: "Product-SO Tracking", icon: Activity, module: "PRODUCT_SO_TRACKING" },
      { id: "user-activity-report", label: "User Activity Report", icon: Activity, module: "AUDIT" },
      { id: "production-sheet",    label: "Production Sheet",     icon: Activity, module: "PRODUCTION_SHEET" },
      { id: "old-stock-tracking",  label: "Old Stock Tracking",  icon: Database, module: "OLD_STOCK_TRACKING" },
      { id: "vendor-po-report",          label: "Vendor PO Report (All)",      icon: FileText, module: "VENDOR_PO_REPORT" },
      { id: "vendor-po-report-cloth",    label: "Cloth Vendor PO Report",      icon: FileText, module: "VENDOR_PO_REPORT" },
      { id: "vendor-po-report-material", label: "Material Vendor PO Report",   icon: FileText, module: "VENDOR_PO_REPORT" },
    ],
  },
  {
    group: "Setup",
    items: [
      { id: "firms",     label: "Firms",          icon: Building2, module: "FIRM" },
      { id: "locations", label: "Locations",      icon: MapPin,    module: "LOCATION" },
      { id: "racks",     label: "Racks",          icon: Warehouse, module: "RACK" },
      { id: "size-chart",label: "Size Chart",     icon: Ruler,     module: "SIZE_CHART" },
      { id: "transport", label: "Transport",      icon: Truck,     module: "TRANSPORT" },
      { id: "hundekari", label: "Hundekari",      icon: HardHat,   module: "HUNDEKARI" },
      { id: "users",     label: "Users",          icon: UserCog,   module: "USER_MANAGEMENT" },
      // pre-seed intentionally left un-moduled so the super admin utility stays visible even with permissions locked down
      { id: "pre-seed",  label: "Pre-Seed Data",  icon: Database },
      { id: "settings",  label: "Settings",       icon: Settings,  module: "SETTINGS" },
      { id: "subscription", label: "Subscription", icon: CreditCard, roles: ["SUPER_ADMIN", "COMPANY_OWNER"] },
      { id: "subscription-settings", label: "Subscription Settings", icon: CalendarClock, roles: ["SUPER_ADMIN"] },
  ],
  },
];

export default NAV;